/* eslint-disable indent */
import { dedupeIdenticalDesignImageThumbnails } from './dedupeDesignImageThumbnails.utils';
import type { ICartItemDetail, IDesignImageThumb } from '@/common/interfaces/cart.interface';

const DESIGN_IMAGE_TYPE_ORDER = [
  'front',
  'back',
  'design_image_primary',
  'design_image_secondary',
] as const;

/**
 * Builds the full thumbnail list from `designImages` plus canonical item fields.
 * Partial API `designImages` arrays must not hide back/design slots when those fields exist.
 */
export const buildDesignImagesFromItem = (item: ICartItemDetail): IDesignImageThumb[] => {
  const byType = new Map<string, IDesignImageThumb>();

  const upsert = (image: IDesignImageThumb): void => {
    if (!image.src) {
      return;
    }
    const existing = byType.get(image.type);
    byType.set(
      image.type,
      existing
        ? {
            ...existing,
            ...image,
            selected: image.selected !== undefined ? image.selected : existing.selected,
          }
        : {
            ...image,
            selected: image.selected ?? false,
          }
    );
  };

  item.designImages?.forEach((image) => {
    upsert({ ...image });
  });

  const front = item.primaryImage || item.frontImage;
  if (front) {
    upsert({ src: front, type: 'front' });
  }
  if (item.backImage) {
    upsert({ src: item.backImage, type: 'back' });
  }
  if (item.designImgPrimary) {
    upsert({ src: item.designImgPrimary, type: 'design_image_primary' });
  }
  if (item.designImgSecondary) {
    upsert({ src: item.designImgSecondary, type: 'design_image_secondary' });
  }

  const images = DESIGN_IMAGE_TYPE_ORDER.map((type) => byType.get(type)).filter(
    (image): image is IDesignImageThumb => Boolean(image)
  );

  if (images.length > 0 && !images.some((image) => image.selected)) {
    const frontImage = images.find((image) => image.type === 'front');
    if (frontImage) {
      frontImage.selected = true;
    } else {
      images[0].selected = true;
    }
  }

  return dedupeIdenticalDesignImageThumbnails(images);
};
